/**
 * Thrown by every module's `useActiveCtx()` adapter when the host app's
 * well-known localStorage keys (`pc.activeOrgId` / `pc.activeStoreId`) are
 * missing at the moment a module page renders.
 *
 * The "happy path" is that the operator app's <RequireActiveContext> guard
 * redirects to /choose-context before the module ever mounts. This error
 * exists for the unhappy edge cases where the guard's React state is out
 * of sync with localStorage:
 *
 *   * An iOS in-app browser (Spark, Gmail webview, Slack, etc.) opened a
 *     deep link; localStorage was wiped between launches but the Supabase
 *     session was restored from a cookie, so the auth gate passes but
 *     the workspace selection is gone.
 *   * Another browser tab called `clear()` (Switch workspace / Sign out)
 *     and this tab's React state is still mid-flight.
 *   * Safari private mode where localStorage.setItem() silently failed
 *     during sign-in so state was set but storage stayed empty.
 *
 * The router-level error element (`<RouterErrorElement>` in `@pc/ui`)
 * catches this and redirects to `/choose-context` instead of showing the
 * generic "something went wrong" screen. We intentionally do NOT report
 * this through the errors pipeline -- it's an expected redirect, not a
 * crash.
 */
export class MissingActiveContextError extends Error {
  /** Stable tag used by `isMissingActiveContextError` so the check
   *  survives bundler instance duplication (multiple copies of the class
   *  in a single bundle would defeat instanceof checks). */
  readonly code = 'MISSING_ACTIVE_CONTEXT' as const;

  /** Which key was missing, in case a caller wants to log it. */
  readonly missing: { orgId: boolean; storeId: boolean };

  constructor(missing: { orgId: boolean; storeId: boolean }) {
    const parts: string[] = [];
    if (missing.orgId) parts.push('orgId');
    if (missing.storeId) parts.push('storeId');
    super(
      `Active workspace missing (${parts.join(', ') || 'unknown'}). ` +
        'The app shell should redirect to /choose-context before rendering modules.',
    );
    this.name = 'MissingActiveContextError';
    this.missing = missing;
  }
}

/**
 * Bundler-safe type guard. We can't rely on `instanceof` alone because the
 * core package gets bundled into every module package and a dev / HMR
 * reload can leave two copies of the class in memory. Falling back to the
 * `code` discriminator makes the guard stable across copies.
 */
export function isMissingActiveContextError(err: unknown): err is MissingActiveContextError {
  if (err instanceof MissingActiveContextError) return true;
  if (err && typeof err === 'object') {
    const code = (err as { code?: unknown }).code;
    if (code === 'MISSING_ACTIVE_CONTEXT') return true;
  }
  return false;
}
